class HeroSlideshow extends HTMLElement {
  constructor() {
    super();
    // ScrollTrigger 기반 스크롤 스냅 효과 제거됨 - 자연스러운 흐름형 스크롤로 변경
  }

  connectedCallback() {
    const root = this;
    const swiperEl = this.querySelector('[data-banner-swiper]');
    if (!swiperEl) return;

    // CDN 캐시 대응: 노출 일정 만료/미시작 슬라이드 제거 (디자인 모드는 미적용)
    if (!window.Shopify || !window.Shopify.designMode) {
      const now = Date.now();
      swiperEl.querySelectorAll('.swiper-slide[data-schedule-start], .swiper-slide[data-schedule-end]').forEach((slide) => {
        const startStr = slide.getAttribute('data-schedule-start');
        const endStr = slide.getAttribute('data-schedule-end');
        const start = startStr ? new Date(startStr.replace(' ', 'T')).getTime() : NaN;
        const end = endStr ? new Date(endStr.replace(' ', 'T')).getTime() : NaN;
        if ((!isNaN(start) && now < start) || (!isNaN(end) && now > end)) {
          slide.remove();
        }
      });
      // 슬라이드가 모두 사라진 경우 swiper 초기화 생략
      if (!swiperEl.querySelector('.swiper-slide')) return;
    }

    // 모바일/PC 구분
    const isMobile = window.matchMedia('(max-width: 749px)').matches;

    const fadeSelector = '[data-fade-up-heading], [data-fade-up-text], [data-fade-up-btn]';
    const arrowsEl = this.querySelector('.slider-arrows');
    const allFadeEls = Array.from(this.querySelectorAll(fadeSelector));
    if (allFadeEls.length) gsap.killTweensOf(allFadeEls);
    allFadeEls.forEach((el) => {
      el.dataset._origTransition = el.style.transition || '';
      el.style.transition = 'none';
    });
    gsap.set(allFadeEls, { y: isMobile ? 15 : 25, opacity: 0 });
    if (arrowsEl) gsap.set(arrowsEl, { y: isMobile ? 15 : 25, opacity: 0 });

    // 동영상 준비되면 포스터 페이드아웃
    const revealVideo = (videoEl) => {
      if (!videoEl) return;
      const container = videoEl.closest('.media-desktop') || videoEl.closest('.media-mobile');
      if (!container) return;
      const poster = container.querySelector('.video-poster');
      if (!poster) return;

      const hide = () => {
        poster.classList.add('is-hidden');
        poster.addEventListener('transitionend', () => poster.remove(), { once: true });
      };

      // readyState >= 3 (HAVE_FUTURE_DATA): 재생 가능할 만큼 데이터 버퍼링됨
      if (videoEl.readyState >= 3) {
        hide();
      } else {
        videoEl.addEventListener('canplay', hide, { once: true });
      }
    };

    // HTML autoplay로 이미 재생 시작된 영상들의 포스터도 처리
    root.querySelectorAll('video').forEach((v) => revealVideo(v));

    const swiper = new Swiper(swiperEl, {
      loop: true,
      speed: isMobile ? 400 : 600, // 모바일에서 더 빠르게
      effect: 'slide',
      autoplay: false,
      navigation: {
        nextEl: this.querySelector('[data-slider-next]'),
        prevEl: this.querySelector('[data-slider-prev]'),
      },
      // 버튼 클릭 시 렉 방지
      preventClicks: false,
      preventClicksPropagation: false,
      touchRatio: isMobile ? 1.5 : 1, // 모바일 터치 감도 향상
      shortSwipes: true,
      longSwipesRatio: 0.3, // 더 짧은 스와이프로 슬라이드 전환
    });

    // Swiper 복제 슬라이드의 비디오 다운로드 방지
    swiperEl.querySelectorAll('.swiper-slide-duplicate video').forEach(video => {
      video.removeAttribute('autoplay');
      video.preload = 'none';
      video.pause();
    });

    // 현재 슬라이드의 비디오 요소 가져오기
    const getActiveVideo = () => {
      const activeSlide = swiper.slides[swiper.activeIndex];
      if (!activeSlide) return null;
      // 모바일/PC에 따라 적절한 비디오 선택
      const mediaContainer = isMobile
        ? activeSlide.querySelector('.media-mobile')
        : activeSlide.querySelector('.media-desktop');
      return mediaContainer?.querySelector('video');
    };

    // 비디오 슬라이드 처리
    const handleVideoSlide = () => {
      const video = getActiveVideo();
      if (video) {
        // 비디오 반복 재생 (영상 끝나도 다음 슬라이드로 넘어가지 않음)
        video.loop = true;

        // 비디오 처음부터 재생
        video.currentTime = 0;
        video.play().catch(() => {});
        revealVideo(video);
      }
    };

    // 슬라이드 변경 시 비디오 처리
    swiper.on('slideChange', () => {
      // 이전 슬라이드 비디오 정지
      const prevSlide = swiper.slides[swiper.previousIndex];
      if (prevSlide) {
        prevSlide.querySelectorAll('video').forEach(v => v.pause());
      }
      handleVideoSlide();
    });

    // 초기 슬라이드 비디오 처리
    handleVideoSlide();

    const restoreTransitions = (els) => {
      els.forEach((el) => {
        if (el && el.dataset._origTransition !== undefined) {
          el.style.transition = el.dataset._origTransition;
          delete el.dataset._origTransition;
        }
      });
    };

    const hideElements = (els) => {
      if (!els || !els.length) return;
      gsap.killTweensOf(els);
      els.forEach((el) => {
        if (el.dataset._origTransition === undefined) el.dataset._origTransition = el.style.transition || '';
        el.style.transition = 'none';
      });
      gsap.set(els, { y: 25, opacity: 0 });
    };

    const runSlideAnimation = (slide) => {
      if (!slide) return;
      const heading = slide.querySelector('[data-fade-up-heading]');
      const texts = Array.from(slide.querySelectorAll('[data-fade-up-text]'));
      const buttons = Array.from(slide.querySelectorAll('[data-fade-up-btn]'));
      const elems = [];
      if (heading) elems.push(heading);
      elems.push(...texts);
      elems.push(...buttons);
      if (elems.length) gsap.killTweensOf(elems);
      elems.forEach((el) => {
        if (el.dataset._origTransition === undefined) el.dataset._origTransition = el.style.transition || '';
        el.style.transition = 'none';
      });
      gsap.set(elems, { y: isMobile ? 15 : 20, opacity: 0 });
      // 제목, 텍스트, 버튼 애니메이션 - 모바일/PC 모두 동시에 나타남
      gsap.to(elems, {
        y: 0,
        opacity: 1,
        duration: isMobile ? 0.4 : 0.5,
        ease: 'power2.out',
        stagger: 0,
        onComplete: () => restoreTransitions(elems)
      });
      // 화살표도 동일한 fade-up 애니메이션
      if (arrowsEl) {
        gsap.killTweensOf(arrowsEl);
        gsap.set(arrowsEl, { y: isMobile ? 15 : 20, opacity: 0 });
        gsap.to(arrowsEl, {
          y: 0,
          opacity: 1,
          duration: isMobile ? 0.4 : 0.5,
          ease: 'power2.out',
        });
      }
    };

    // Swiper tap 이벤트로 클릭/드래그 구분 (Swiper 내장 처리)
    swiper.on('tap', (s, event) => {
      if (event.target.closest('a, button')) return;

      const slideEl = event.target.closest('.swiper-slide');
      if (!slideEl) return;
      const link = slideEl.getAttribute('data-slide-link') || '';

      // dataLayer: main_click (banner)
      if (typeof FilaDL !== 'undefined') {
        var headingEl = slideEl.querySelector('.banner__heading');
        var bannerName = headingEl ? FilaDL.stripHTML(headingEl.innerHTML) : '';
        var imgEl = slideEl.querySelector('.banner__media-img') || slideEl.querySelector('.video-poster') || slideEl.querySelector('video');
        var imgUrl = imgEl ? (imgEl.currentSrc || imgEl.src || '') : '';

        FilaDL.push({
          event: 'main_click',
          click: {
            main_box_name: 'hero_banner',
            type: 'banner',
            banner_event_name: 'hero banner',
            banner_name: bannerName,
            img_url: imgUrl,
            index: swiper.realIndex,
            click_location: link,
            page_type: FilaDL.getMainPageType()
          }
        });
      }

      if (link) window.location.href = link;
    });

    swiper.on('slideChangeTransitionStart', () => {
      const nowAll = Array.from(root.querySelectorAll(fadeSelector));
      hideElements(nowAll);
      if (arrowsEl) {
        gsap.killTweensOf(arrowsEl);
        gsap.set(arrowsEl, { y: 25, opacity: 0 });
      }
    });

    swiper.on('slideChangeTransitionEnd', () => {
      const activeSlide = swiper.slides[swiper.activeIndex];
      runSlideAnimation(activeSlide);
    });

    requestAnimationFrame(() => {
      const firstSlide = swiper.slides[swiper.activeIndex];
      runSlideAnimation(firstSlide);

      // 초기 로드 시 스와이프 가능 힌트: 살짝 왼쪽으로 밀었다 복귀
      if (!root.hasAttribute('data-no-swipe-hint')) {
        const peekDistance = isMobile ? window.innerWidth * 0.4 : window.innerWidth * 0.3;
        const wrapperEl = swiperEl.querySelector('.swiper-wrapper');
        if (wrapperEl) {
          gsap.to(wrapperEl, {
            x: -peekDistance,
            duration: 0.6,
            delay: 0,
            ease: 'power2.out',
            onComplete: () => {
              gsap.to(wrapperEl, {
                x: 0,
                duration: 0.5,
                ease: 'power2.inOut',
              });
            }
          });
        }
      }
    });
  }
}

customElements.define('hero-slideshow', HeroSlideshow);


